import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, formatApiErrorDetail } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { ExternalLink, CheckCircle2, MessageSquare, ArrowLeft } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useParams } from "react-router-dom";

const INR = (n) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(n || 0);

const STATUS_LABEL = {
  initiated: { bg: "bg-amber-100 text-amber-800", label: "Initiated" },
  under_review: { bg: "bg-blue-100 text-blue-800", label: "Under Review" },
  clarification_requested: { bg: "bg-orange-100 text-orange-800", label: "Clarification Requested" },
  accepted: { bg: "bg-emerald-100 text-emerald-800", label: "Accepted" },
  cancelled: { bg: "bg-zinc-100 text-zinc-700", label: "Cancelled" },
};

function Pill({ status }) {
  const s = STATUS_LABEL[status] || { bg: "bg-zinc-100 text-zinc-700", label: status };
  return <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium uppercase tracking-widest ${s.bg}`}>{s.label}</span>;
}

export default function Handovers({ inbox = false }) {
  const { user } = useAuth();
  const { data: rows = [] } = useQuery({
    queryKey: ["handovers", inbox],
    queryFn: async () => (await api.get("/handovers", { params: inbox ? { mine: 1 } : {} })).data,
  });
  const { data: clients = [] } = useQuery({ queryKey: ["clients"], queryFn: async () => (await api.get("/clients")).data });
  const clientName = (id) => clients.find((c) => c.id === id)?.name || "—";

  return (
    <div data-testid={inbox ? "handover-inbox-page" : "handovers-page"}>
      <PageHeader
        eyebrow={inbox ? "Operations Division · Inbox" : "Business Division · Handover"}
        title={inbox ? "Handover Inbox" : "Handovers"}
        description={inbox
          ? "Incoming handover packages from the Managing Director awaiting your review."
          : "Formal ownership transitions from Business to Operations. Each handover creates an auditable event and (on acceptance) a Project."}
      />
      <div className="max-w-[1400px] mx-auto px-8 py-6">
        <div className="border border-zinc-200 rounded-lg bg-white overflow-hidden" data-testid="handovers-table">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 border-b border-zinc-200">
              <tr className="text-left text-[10px] uppercase tracking-widest text-zinc-500">
                <th className="px-4 py-3">Number</th><th className="px-4 py-3">Client</th><th className="px-4 py-3">Priority</th>
                <th className="px-4 py-3">Status</th><th className="px-4 py-3">Initiated</th><th></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100">
              {rows.map((h) => (
                <tr key={h.id} data-testid={`ho-row-${h.id}`}>
                  <td className="px-4 py-3 font-mono text-xs">{h.number}</td>
                  <td className="px-4 py-3 font-medium">{clientName(h.client_id)}</td>
                  <td className="px-4 py-3"><Badge variant="outline" className="capitalize">{h.priority}</Badge></td>
                  <td className="px-4 py-3"><Pill status={h.status} /></td>
                  <td className="px-4 py-3 font-mono text-xs text-zinc-500">{h.initiated_at?.slice(0, 10)}</td>
                  <td className="px-4 py-3 text-right"><Link to={`/app/handovers/${h.id}`} className="text-xs inline-flex items-center gap-1 hover:text-zinc-900" data-testid={`ho-open-${h.id}`}>Open <ExternalLink className="w-3 h-3" /></Link></td>
                </tr>
              ))}
              {rows.length === 0 && <tr><td colSpan={6} className="px-4 py-12 text-center text-sm text-zinc-500">{inbox ? "Inbox is clear." : "No handovers yet. Initiate one from an approved quotation in Client 360°."}</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export function HandoverDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const qc = useQueryClient();
  const { user } = useAuth();
  const [comment, setComment] = useState("");
  const { data, isLoading } = useQuery({ queryKey: ["handover", id], queryFn: async () => (await api.get(`/handovers/${id}`)).data });

  const acceptMut = useMutation({
    mutationFn: () => api.post(`/handovers/${id}/accept`),
    onSuccess: (r) => {
      toast.success(`Accepted · Project ${r.data.project.code} created`);
      qc.invalidateQueries();
      nav(`/app/projects/${r.data.project.id}`);
    },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });
  const clarifyMut = useMutation({
    mutationFn: () => api.post(`/handovers/${id}/request-clarification`, { text: comment }),
    onSuccess: () => { toast.success("Clarification requested"); setComment(""); qc.invalidateQueries({ queryKey: ["handover", id] }); qc.invalidateQueries({ queryKey: ["notifications"] }); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });
  const commentMut = useMutation({
    mutationFn: () => api.post(`/handovers/${id}/comment`, { text: comment }),
    onSuccess: () => { setComment(""); qc.invalidateQueries({ queryKey: ["handover", id] }); },
  });

  if (isLoading || !data) return <div className="p-12 text-sm text-zinc-500 font-mono">Loading handover…</div>;
  const { handover: h, client, quotation, estimation, project } = data;
  const canAccept = user?.role === "operations_manager" && h.status !== "accepted";

  return (
    <div data-testid="handover-detail">
      <PageHeader
        eyebrow={<Link to="/app/handovers" className="hover:underline inline-flex items-center gap-1"><ArrowLeft className="w-3 h-3" /> All Handovers</Link>}
        title={<span className="flex items-center gap-3">{h.number} <Pill status={h.status} /></span>}
        description={<span>{client?.name} · <span className="capitalize">{h.priority} priority</span></span>}
        actions={
          canAccept && (
            <Button onClick={() => acceptMut.mutate()} disabled={acceptMut.isPending} data-testid="ho-accept">
              <CheckCircle2 className="w-4 h-4 mr-1.5" /> Accept & Create Project
            </Button>
          )
        }
      />
      <div className="max-w-[1400px] mx-auto px-8 py-6 grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-4">
          <div className="border border-zinc-200 rounded-lg bg-white p-6">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Handover Package</div>
            <div className="mt-3 grid grid-cols-2 gap-4 text-sm">
              <div><div className="text-[10px] uppercase tracking-widest text-zinc-500">Quotation</div><div className="font-mono">{quotation?.number}</div><div className="text-zinc-700 mt-0.5">{quotation?.title}</div><div className="font-mono text-emerald-700 mt-0.5">{INR(quotation?.total)}</div></div>
              <div><div className="text-[10px] uppercase tracking-widest text-zinc-500">Estimation</div><div className="font-mono">{estimation?.number || "—"}</div><div className="text-zinc-700 mt-0.5">{estimation?.total_hours ? `${estimation.total_hours} estimated hours` : ""}</div></div>
              <div><div className="text-[10px] uppercase tracking-widest text-zinc-500">Target Start</div><div className="font-mono">{h.target_start_date || "—"}</div></div>
              <div><div className="text-[10px] uppercase tracking-widest text-zinc-500">Target End</div><div className="font-mono">{h.target_end_date || "—"}</div></div>
            </div>
            {h.package_notes && (
              <div className="mt-4 border-t border-zinc-100 pt-4">
                <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Package Notes</div>
                <div className="mt-1 text-sm text-zinc-700 leading-relaxed whitespace-pre-wrap">{h.package_notes}</div>
              </div>
            )}
            {project && (
              <div className="mt-4 border-t border-zinc-100 pt-4">
                <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Created Project</div>
                <Link to={`/app/projects/${project.id}`} className="font-mono text-sm underline underline-offset-2">{project.code} · {project.name}</Link>
              </div>
            )}
          </div>

          <div className="border border-zinc-200 rounded-lg bg-white p-6">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Discussion</div>
            <div className="mt-3 space-y-3">
              {(h.comments || []).map((c) => (
                <div key={c.id} className="border-l-2 border-zinc-200 pl-3">
                  <div className="text-xs"><span className="font-medium">{c.author_name}</span> <span className="font-mono text-[10px] text-zinc-400">· {c.at.slice(0, 16).replace("T", " ")}</span></div>
                  <div className="text-sm text-zinc-700 mt-1 whitespace-pre-wrap">{c.text}</div>
                </div>
              ))}
              {(h.comments || []).length === 0 && <div className="text-sm text-zinc-500">No discussion yet.</div>}
            </div>
            <div className="mt-4">
              <Textarea value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Add a comment or request clarification…" data-testid="ho-comment-input" />
              <div className="mt-2 flex gap-2">
                <Button size="sm" variant="outline" disabled={!comment.trim()} onClick={() => commentMut.mutate()} data-testid="ho-comment-add">
                  <MessageSquare className="w-3 h-3 mr-1" /> Add Comment
                </Button>
                {user?.role === "operations_manager" && h.status !== "accepted" && (
                  <Button size="sm" variant="outline" disabled={!comment.trim()} onClick={() => clarifyMut.mutate()} data-testid="ho-clarify">
                    Request Clarification
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
        <div className="space-y-4">
          <div className="border border-zinc-200 rounded-lg bg-white p-5">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Timeline</div>
            <ol className="mt-3 space-y-3 text-sm">
              <li className="flex items-start gap-3"><div className="w-2 h-2 rounded-full bg-emerald-500 mt-1.5" /><div><div className="font-medium">Initiated by MD</div><div className="text-[11px] text-zinc-500 font-mono">{h.initiated_at?.slice(0, 16).replace("T", " ")}</div></div></li>
              {h.status === "clarification_requested" && <li className="flex items-start gap-3"><div className="w-2 h-2 rounded-full bg-orange-500 mt-1.5" /><div><div className="font-medium">Clarification Requested</div><div className="text-[11px] text-zinc-500 font-mono">by {h.reviewed_by_user_id}</div></div></li>}
              {h.accepted_at && <li className="flex items-start gap-3"><div className="w-2 h-2 rounded-full bg-emerald-600 mt-1.5" /><div><div className="font-medium">Accepted by Ops</div><div className="text-[11px] text-zinc-500 font-mono">{h.accepted_at?.slice(0, 16).replace("T", " ")}</div></div></li>}
            </ol>
          </div>
        </div>
      </div>
    </div>
  );
}
